'use strict';

/**
 * @ngdoc type
 * @name mx.shell.ToolbarManager
 * @module mx.shell
 * @restrict 'E'
 * @scope {}
 * @requires {@ref mx.shell.Config mx.shell.Config}
 * @requires {@ref mx.shell.EventService mx.shell.EventService}
 * @requires {@ref mx.shell.UserProfileService mx.shell.UserProfileService}
 * @requires {@ref mx.shell.FeedbackProviderService mx.shell.FeedbackProviderService}
 * @requires {@ref mx.shell.ApplicationProviderService mx.shell.ApplicationProviderService}
 * @requires {@ref mx.shell.InfoPanelService mx.shell.InfoPanelService}
 *
 * @property {string} avatar - Gets or sets user's avatar url
 * @property {string} firstName - Gets or sets user's first name
 * @property {string} lastName - Gets or sets user's last name
 * @property {boolean} isFeedbackVisible - Gets or sets if feedback button is visible
 * @property {boolean} FeedbackEnabled - Gets or sets if feedback is enabled
 * @property {boolean} isAvatarVisible - Gets or sets if user info is visible
 * @property {boolean} isSearchVisible - Gets or sets if search bar is visible
 * @property {boolean} isShowMenuBtnVisible - Gets or sets if show menu button is visible
 *
 * @description
 * A factory that produces Toolbar Manager object that is used to manage the visibility or availability of toolbar elements
 */

export class ToolbarManager {
	static $inject = [
		'mx.shell.Config',
		'mx.shell.EventService',
		'mx.shell.UserProfileService',
		'mx.shell.FeedbackProviderService',
		'mx.shell.AppsService',
		'mx.shell.InfoPanelService',
	];

	constructor(configService, eventService, userProfileService, feedbackProviderService, appsService, infoPanelService) {
		Object.assign(this, { configService, eventService, userProfileService, feedbackProviderService, appsService, infoPanelService });
	}

	init() {
		Object.defineProperty(this, 'isInfoPanelVisible', {
			get: function () {
				return this.infoPanelService.widgets.length > 0;
			},
		});

		const cfg = this.configService.get('toolbar') || {};

		this.isAppsVisible = this.AppsEnabled = this.appsService.isEnabled();

		this.operationMonitorEnabled = Boolean(cfg.showOperationMonitor);
		this.isOperationMonitorVisible = this.operationMonitorEnabled;

		this.appsService.getCurrentApplication().then((app) => {
			this.title = app.title;
			this.setSearchEnabled(Boolean(cfg.showSearch) && !app.unifiedSearchEnabled);
			this.setOperationMonitorVisibility(app.showOperationMonitor);
		});

		this.avatarEnabled = Boolean(cfg.showAvatar);

		this.title = undefined;

		this.userProfileService.get().then((profile) => {
			this.setUserInfo(profile);

			this.eventService.subscribeGlobal(mx.shell.EventCodes.ProfileChanged, (event, profile) => {
				this.setUserInfo(profile);
			});
		});

		this.isPushNotificationsWanted = true;
		this.isPushNotificationsEnabled = this.isPushNotificationsVisible = false;

		this.isAvatarVisible = this.avatarEnabled;

		this.avatarDropdownItems = [];
	}

	setToolbarVisibility(isVisible) {
		this.isVisible = isVisible;
	}

	setFeedbackVisibility(isVisible) {
		this.isFeedbackVisible = this.FeedbackEnabled && isVisible;
	}

	setAppsVisibility(isVisible) {
		this.isAppsVisible = this.AppsEnabled && isVisible;
	}

	setAvatarVisibility(isVisible) {
		this.isAvatarVisible = this.avatarEnabled && isVisible;
	}

	setSearchVisibility(isVisible) {
		this.isSearchWanted = isVisible;
		this.isSearchVisible = this.isSearchEnabled && this.isSearchWanted;
	}

	setSearchEnabled(isEnabled) {
		this.isSearchEnabled = isEnabled;
		this.isSearchVisible = this.isSearchEnabled && this.isSearchWanted;
	}

	setOperationMonitorVisibility(isVisible) {
		this.isOperationMonitorVisible = this.operationMonitorEnabled && isVisible;
	}

	setShowMenuButtonVisibility(isVisible) {
		this.isShowMenuBtnVisible = isVisible;
	}

	setBreadcrumbsVisibility(isVisible) {
		this.isBreadcrumbsVisible = isVisible;
	}

	setNavigationVisibility(isVisible) {
		this.isNavigationVisible = isVisible;
	}

	setPushNotificationsVisibility(isVisible) {
		this.isPushNotificationsWanted = isVisible;
		this.isPushNotificationsVisible = isVisible && this.isPushNotificationsEnabled;
	}

	setPushNotificationsEnabled(isEnabled) {
		this.isPushNotificationsEnabled = isEnabled;
		this.isPushNotificationsVisible = this.isPushNotificationsWanted && this.isPushNotificationsEnabled;
	}

	setUserInfo(profile) {
		this.avatar = profile ? profile.avatar : null;
		this.lastName = profile ? profile.lastName : null;
		this.firstName = profile ? profile.firstName : null;
		this.email = profile ? profile.email : null;
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ToolbarManager#addAvatarDropdownItem
	 *
	 * @description
	 * Add new item to Dropdown which opens on avatar click on top right corner.<br>
	 *
	 * @param {object} itemObject - object for defining dropdown item.
	 * Example 1: {
	 *     icon: "save",
	 *     label: "Save All",
	 *     position: 20,
	 *     htmlClass: 'my-item-html-class',
	 *     onItemClick: function () { ... }
	 * }
	 *
	 * Example 2: {
	 *     icon: "supervisor_account",
	 *     label: "Select account...",
	 *     children: [{
	 *          icon: "check",
	 *          label: "My account 1",
	 *          isChecked: true,
	 *          onItemClick: function () { ... }
	 *     }, {
	 *          icon: "check",
	 *          label: "My account 2",
	 *          isChecked: false,
	 *          onItemClick: function () { ... }
	 *     } ... ]
	 * }
	 *
	 */
	addAvatarDropdownItem(itemObject) {
		this.avatarDropdownItems.push(itemObject);
		this.avatarDropdownItems.sort(function (a, b) {
			return a.position !== undefined && b.position !== undefined ? Number(a.position) - Number(b.position) : 0;
		});

		this.eventService.dispatchEvent(mx.shell.EventCodes.AvatarMenuItemAdded, itemObject);
		return itemObject;
	}
}

angular.module('mx.shell').service('mx.shell.ToolbarManager', ToolbarManager);
